<template>
  <div
    id="header"
    class="lg:h-[80px] xl:mb-[38px]"
    :class="{
      'pb-8': isMobile && isCheckoutPage && showCheckoutPageNewLayout,
      'sticky z-50': !isCheckoutPage || !showCheckoutPageNewLayout,
    }"
    :style="navigationTop"
  >
    <div
      v-if="isMobile && isCheckoutPage && showCheckoutPageNewLayout"
      class="border-b border-black-10 bg-white p-2"
    >
      <HeaderBenefits />
    </div>
    <div
      class="header-grid-container grid overscroll-none border-b border-black-10 bg-white lg:border-0 lg:bg-platin"
    >
      <div
        v-if="
          isMobile ||
          (!isMobile && !(isCheckoutPage && showCheckoutPageNewLayout))
        "
        class="mobile__navigation-container mx-3 gap-x-3"
      >
        <div class="mobile__burger-button-container">
          <Button
            class="mobile__burger-button-container__burger-button"
            type="ghost"
            data-test-id="side-navigation-button"
            @click="openSideNavigationMenu"
          >
            <IconBurger />
            <span
              class="mobile__burger-button-container__burger-button__text leading-none"
            >
              Menu
            </span>
          </Button>
        </div>

        <LogoMobile />

        <div class="container">
          <div class="flex w-full items-center justify-between">
            <LazyTopMetaBar
              v-if="blok && blok.TopMetaBar"
              class="desktop__top-header-meta-bar"
              :blok="blok.TopMetaBar"
            />
            <LazyHeaderMainMenu class="desktop__top-header-main-menu" />
          </div>
        </div>
      </div>

      <div
        v-if="!isMobile && isCheckoutPage && showCheckoutPageNewLayout"
        class="flex items-center bg-white p-4"
      >
        <div class="w-1/5">
          <Logo :size="isHeaderMinified ? 'sm' : 'md'" />
        </div>
        <div class="w-3/5 self-center">
          <HeaderBenefits
            v-if="!isMobile && isCheckoutPage && showCheckoutPageNewLayout"
          />
        </div>
      </div>

      <div
        v-if="!isMobile && !(isCheckoutPage && showCheckoutPageNewLayout)"
        class="desktop__header-container"
      >
        <div class="container relative px-5">
          <LazyHeaderSubNavigation
            :minified="isHeaderMinified"
            :categories="rootCategories"
            :fetching="fetchingCategories"
            :blok="blok"
            :flyout-menu-category-id="flyoutMenuCategoryId"
            @mouseenter:item="handleMouseEnterItem"
            @mouseleave:navigation="handleMouseLeaveNavigation"
            @click:item="forceCloseFlyout"
          />

          <LazyFlyoutMenu
            v-for="category in mainNavigationCategories"
            :key="category.id"
            :class="{
              'lg:-mt-2 xl:-mt-5': !isHeaderMinified,
            }"
            :is-open="category.id === flyoutMenuCategoryId"
            :blok="blok"
            :category="category"
            @mouseleave="handleMouseLeaveNavigation"
            @mouseenter="preventClosing"
          />

          <Backdrop
            v-if="hasVisibleFlyout"
            show
            class="inset-x-0 size-full"
            :inset="false"
          />
        </div>
      </div>
    </div>
    <slot />
  </div>
</template>

<script setup lang="ts">
import { debounce } from 'es-toolkit'
import type { Category } from '@scayle/storefront-nuxt'
import { isHiddenForMainNavigation } from '~/utils/category'
import { useRoute } from '#app/composables/router'
import type { SbNavigation } from '~/cms/storyblok/types/storyblok'
import type { NavigationItem } from '~/types/navigation'

const props = defineProps({
  rootCategories: {
    type: Array as PropType<Category[]>,
    default: () => [],
  },
  fetchingCategories: {
    type: Boolean as PropType<boolean>,
    default: () => false,
  },
  forceMinified: {
    type: Boolean as PropType<boolean>,
    default: () => false,
  },
  blok: {
    type: Object as PropType<SbNavigation>,
    default: () => {},
  },
  stickToBanner: {
    type: Boolean as PropType<boolean>,
    default: () => false,
  },
})

const route = useRoute()

const {
  isFlyoutMenuOpen,
  closeFlyoutMenu,
  openSideNavigation,
  openFlyoutMenu,
  bannerHeight,
  flyoutMenuCategoryId,
  setFlyoutMenuCategories,
} = useUiState()

const { showCheckoutPageNewLayout } = useExperiments()

const FLYOUT_DELAY = 250

const openFlyoutMenuDebounced = debounce(openFlyoutMenu, FLYOUT_DELAY)

const closeFlyoutMenuDebounced = debounce(closeFlyoutMenu, FLYOUT_DELAY)

const hasNavigationFlyoutContent = (item: Category | NavigationItem) => {
  const match = props.blok?.MainNavigation?.find(
    (obj) =>
      obj.RootCategoryID === item.id.toString() ||
      obj.label?.toLowerCase() === item.name?.toLowerCase(),
  )
  return Boolean(
    match?.NavigationFlyoutContent?.length ||
      match?.NavigationItem?.length ||
      match?.ClickableImage?.length,
  )
}

const { getNavigationItems } = useNavigationMapping()
const mainNavigationCategories = computed<NavigationItem[]>(() => {
  return getNavigationItems(
    props.blok?.MainNavigation || [],
    props.rootCategories,
  )
})

if (mainNavigationCategories.value) {
  setFlyoutMenuCategories(mainNavigationCategories.value)
}

const hasVisibleFlyout = computed(() => {
  if (!isFlyoutMenuOpen.value || flyoutMenuCategoryId.value === -1) {
    return false
  }

  const currentCategory = mainNavigationCategories.value.find(
    (cat) => cat.id === flyoutMenuCategoryId.value,
  )

  if (!currentCategory) return false

  // Check if there's a Storyblok match with content
  const storyblokMatch = props.blok?.MainNavigation?.find((item) => {
    return item.RootCategoryID === currentCategory.id.toString()
  })

  if (storyblokMatch) {
    const hasNavigationItems = (storyblokMatch.NavigationItem?.length || 0) > 0
    const hasFlyoutContent =
      (storyblokMatch.NavigationFlyoutContent?.length || 0) > 0
    const hasClickableImages = (storyblokMatch.ClickableImage?.length || 0) > 0
    const hasScayleCategories =
      storyblokMatch.RootCategoryID &&
      currentCategory.children?.some(
        (child) => !isHiddenForMainNavigation(child),
      )

    return (
      hasNavigationItems ||
      hasFlyoutContent ||
      hasClickableImages ||
      hasScayleCategories
    )
  }

  return false
})

const handleMouseEnterItem = (navigationItem: NavigationItem) => {
  closeFlyoutMenuDebounced.cancel()

  const storyblokItem = props.blok?.MainNavigation?.find(
    (item) =>
      item.RootCategoryID === navigationItem.id.toString() ||
      item.label?.toLowerCase() === navigationItem.name?.toLowerCase(),
  )

  if (
    navigationItem.children?.length > 0 ||
    hasNavigationFlyoutContent(navigationItem)
  ) {
    const enrichedNavigationItem = {
      ...navigationItem,
      NavigationItem: storyblokItem?.NavigationItem || [],
      NavigationFlyoutContent: storyblokItem?.NavigationFlyoutContent || [],
      ClickableImage: storyblokItem?.ClickableImage || [],
    }

    if (isFlyoutMenuOpen.value) {
      openFlyoutMenu(enrichedNavigationItem)
    } else {
      openFlyoutMenuDebounced(enrichedNavigationItem)
    }
  } else {
    closeFlyoutMenu()
  }
}

const handleMouseLeaveNavigation = () => {
  openFlyoutMenuDebounced.cancel()
  closeFlyoutMenuDebounced()
}

const preventClosing = closeFlyoutMenuDebounced.cancel

const forceCloseFlyout = (event: MouseEvent) => {
  openFlyoutMenuDebounced.cancel()
  closeFlyoutMenuDebounced.cancel()
  closeFlyoutMenu(event, true)
}

const { isMobile } = useDeviceType()

const isCheckoutPage = computed(() => route.fullPath.includes('checkout'))

const isScrolled = ref(false)

const handleScroll = () => {
  isScrolled.value = window.scrollY > 0
}

const isHeaderMinified = computed(() => props.forceMinified || isScrolled.value)

onMounted(() => {
  if (import.meta.client) {
    window.addEventListener('scroll', handleScroll, { passive: true })
  }
})

onBeforeUnmount(() => {
  if (import.meta.client) {
    window.removeEventListener('scroll', handleScroll)
  }
})

const openSideNavigationMenu = () => {
  openSideNavigation()
}

const navigationTop = computed(() => ({
  top:
    props.stickToBanner && !isCheckoutPage.value
      ? `${bannerHeight.value}px`
      : '0px',
}))
</script>

<style lang="postcss" scoped>
.header-grid-container {
  grid-template-columns: 1fr;
  grid-template-rows: 2;
  grid-template-areas:
    'mobile'
    'desktop';
}

.mobile__navigation-container {
  @apply flex justify-between items-center
  py-[1px] mt-0
  max-h-[54px];

  grid-area: mobile;
}

.mobile__logo {
  @apply hidden lg:inline-block;
}

.mobile__burger-button-container {
  @apply lg:hidden flex justify-between items-center;
}

.mobile__burger-button-container__burger-button {
  @apply 2xl:hidden
  flex flex-col
  gap-0
  h-[3.375rem]
  mr-auto
  -ml-3
  mx-3
  px-3;
}

.mobile__burger-button-container__burger-button__text {
  @apply text-xs uppercase;
}
.desktop__top-header-meta-bar {
  @apply hidden
  lg:flex;
}
.desktop__top-header-main-menu {
  @apply ml-auto;
}
.desktop__header-container {
  @apply hidden lg:block bg-white;

  grid-area: desktop;
}
</style>
